import {
  WSCard,
  WSElement,
  WSGrid,
  WSList,
  WSLoader,
  WSText
} from "@wingspanhq/fe-component-library";
import { Currency } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useMemo } from "react";
import { DataItem } from "../../../shared/components/DataItem";
import { useProfitAndLossQuery } from "../components/ProfitAndLoss/useProfitAndLossQuery";
import { useTaxEstimateQuery } from "../components/ProfitAndLoss/useTaxEstimateQuery";
import { CardTaxReadyDocuments } from "../components/CardTaxReadyDocuments";
import {
  CardAddTransactions,
  CardShareWithAccountant
} from "../components/SidebarCards";
import { useModalAddTransaction } from "../modals/ModalAddTransaction";

const TOP_COUNT = 5;
const money = (value?: string | number) =>
  formatMoney(Number(value ?? 0), Currency.USD);

const ReportCard: React.FC<{
  label: string;
  loading: boolean;
  items: { category?: string; amount?: string | number }[];
  onAddTransactions: () => void;
}> = props => (
  <WSCard header={{ label: props.label }}>
    {props.loading ? (
      <WSElement my="XL">
        <WSLoader.Spinner />
      </WSElement>
    ) : props.items.length > 0 ? (
      <WSList gap="M">
        {props.items.map((item, index) => (
          <DataItem
            key={`${item.category ?? index}`}
            label={item.category || "Uncategorized"}
            value={money(item.amount)}
          />
        ))}
      </WSList>
    ) : (
      <WSText.ParagraphSm color="gray600">
        No deductible expenses yet.{" "}
        <WSText.ParagraphSm
          inline
          color="blue500"
          onClick={props.onAddTransactions}
        >
          Add transactions.
        </WSText.ParagraphSm>
      </WSText.ParagraphSm>
    )}
  </WSCard>
);

export const RouteBookkeepingReports: React.FC = () => {
  const { query, range } = useProfitAndLossQuery();
  const year = range.start.getFullYear();
  const queryTaxEstimate = useTaxEstimateQuery(year);
  const modalAddTransaction = useModalAddTransaction();

  const openAddTransaction = () => {
    void modalAddTransaction.open({});
  };

  const topExpenses = useMemo(
    () =>
      [...(query.data?.expenseByCategory ?? [])]
        .sort((a, b) => Number(b.amount ?? 0) - Number(a.amount ?? 0))
        .slice(0, TOP_COUNT),
    [query.data]
  );

  const expenses = Number(query.data?.totalExpenses ?? 0);
  // effectiveTaxRate is a percentage (e.g. 24.5) — the authoritative rate from
  // the estimate, replacing the old total-tax ÷ profit approximation.
  const effectiveTaxRate = queryTaxEstimate.data?.effectiveTaxRate ?? 0;
  const projectedSavings = expenses * (effectiveTaxRate / 100);

  return (
    <WSGrid gutter="2XL">
      <WSGrid.Item span={{ xs: "12", m: "8" }}>
        <WSList gap="2XL">
          <WSText.Heading1>Reports</WSText.Heading1>
          <ReportCard
            label="Top expense categories"
            loading={query.isLoading}
            items={topExpenses}
            onAddTransactions={openAddTransaction}
          />
        </WSList>
      </WSGrid.Item>
      <WSGrid.Item span={{ xs: "12", m: "4" }}>
        <WSList gap="2XL">
          <CardAddTransactions />
          <CardTaxReadyDocuments
            year={year}
            savings={projectedSavings}
            loading={queryTaxEstimate.isLoading}
          />
          <CardShareWithAccountant />
        </WSList>
      </WSGrid.Item>
    </WSGrid>
  );
};
